/**
 * Customer Service - Production Ready
 * Handles all Customer-related API calls using RESTful Web API endpoints
 * Backend API: /web/master/customer
 *
 * Endpoints:
 * - GET    /web/master/customer              - List all customers
 * - GET    /web/master/customer/:id          - Get customer by ID
 * - POST   /web/master/customer              - Create customer
 * - PUT    /web/master/customer/:id          - Update customer
 * - DELETE /web/master/customer/:id          - Delete customer
 * - POST   /web/master/customer/bulk         - Bulk operations
 */

import apiService from 'core/services/apiClient';
import logger from 'core/utils/logger';
import {
  Customer,
  CustomerFormData,
  CustomerListFilters,
  customerStats,
  customerListResponse,
  CustomerMainType,
  CustomerSubType,
  Route,
  Beat,
  SalesPerson,
  Territory,
} from '../types';

const BASE_URL = '/web/master/customer';

/**
 * Transform customer form data to match backend DTO conventions:
 * - Top-level fields: keep snake_case (customer_code, customer_name, etc.)
 * - Nested arrays (contacts, documents): convert to camelCase (contactName, documentType)
 * - Remove unsupported fields (file, dateOfBirth)
 */
const transformcustomerData = (data: any): any => {
  const transformed: any = { ...data };

  // Note: territory_id is now supported by backend, so we keep it

  // Transform contacts array to camelCase
  if (transformed.contacts && Array.isArray(transformed.contacts)) {
    transformed.contacts = transformed.contacts.map((contact: any) => {
      const c: any = {};
      if (contact.contact_name || contact.contactName) c.contactName = contact.contact_name || contact.contactName;
      if (contact.phone) c.phone = contact.phone;
      if (contact.email) c.email = contact.email;
      if (contact.designation) c.designation = contact.designation;
      if (contact.alternate_phone || contact.alternatePhone) c.alternatePhone = contact.alternate_phone || contact.alternatePhone;
      if (contact.is_primary !== undefined || contact.isPrimary !== undefined) c.isPrimary = contact.is_primary ?? contact.isPrimary;
      if (contact.is_decision_maker !== undefined || contact.isDecisionMaker !== undefined) c.isDecisionMaker = contact.is_decision_maker ?? contact.isDecisionMaker;
      // Note: dateOfBirth is NOT in backend DTO, so we skip it
      return c;
    });
  }

  // Transform documents array to camelCase
  if (transformed.documents && Array.isArray(transformed.documents)) {
    transformed.documents = transformed.documents.map((doc: any) => {
      const d: any = {};
      if (doc.document_type || doc.documentType) d.documentType = doc.document_type || doc.documentType;
      if (doc.document_name || doc.documentName) d.documentName = doc.document_name || doc.documentName;
      if (doc.document_number || doc.documentNumber) d.documentNumber = doc.document_number || doc.documentNumber;
      if (doc.issue_date || doc.issueDate) d.issueDate = doc.issue_date || doc.issueDate;
      if (doc.expiry_date || doc.expiryDate) d.expiryDate = doc.expiry_date || doc.expiryDate;
      if (doc.remarks) d.remarks = doc.remarks;
      // Note: file is NOT in backend DTO, so we skip it
      return d;
    });
  }

  // Transform addresses array to camelCase (if present)
  if (transformed.addresses && Array.isArray(transformed.addresses)) {
    transformed.addresses = transformed.addresses.map((addr: any) => {
      const a: any = {};
      if (addr.address_type || addr.addressType) a.addressType = addr.address_type || addr.addressType;
      if (addr.address) a.address = addr.address;
      if (addr.city) a.city = addr.city;
      if (addr.state) a.state = addr.state;
      if (addr.district) a.district = addr.district;
      if (addr.country) a.country = addr.country;
      if (addr.pincode) a.pincode = addr.pincode;
      if (addr.landmark) a.landmark = addr.landmark;
      if (addr.gst_number || addr.gstNumber) a.gstNumber = addr.gst_number || addr.gstNumber;
      if (addr.is_default !== undefined || addr.isDefault !== undefined) a.isDefault = addr.is_default ?? addr.isDefault;
      return a;
    });
  }

  return transformed;
};

class CustomerServiceImpl {
  // ========== CRUD Operations ==========

  /**
   * Get paginated customer list with filters
   * Endpoint: GET /web/customer-master
   */
  async getCustomerList(
    page: number = 1,
    limit: number = 10,
    filters?: CustomerListFilters
  ): Promise<customerListResponse> {
    try {
      logger.info('Fetching customer list', { page, limit, filters });

      const params = {
        page,
        limit,
        ...filters,
      };

      const response = await apiService.get(BASE_URL, { params });

      // Backend response structure (after apiClient unwrapping for paginated):
      // { data: [...customers...], pagination: { page, limit, total, totalPages, hasNextPage, ... } }

      const list = response?.data || [];
      const pagination = response?.pagination || {};

      return {
        list: list,
        total: pagination.total || 0,
        page: pagination.page || page,
        limit: pagination.limit || limit,
        pages: pagination.totalPages || 0,
      };
    } catch (error) {
      logger.error('Failed to fetch customer list', error);
      throw error;
    }
  }

  /**
   * Get customer details by ID
   * Endpoint: GET /web/customer-master/:id
   */
  async getCustomerDetail(id: string | number): Promise<Customer> {
    try {
      logger.info('Fetching customer detail', { id });

      const response = await apiService.get(`${BASE_URL}/${id}`);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch customer detail', error);
      throw error;
    }
  }

  /**
   * Create new customer
   * Endpoint: POST /web/customer-master
   */
  async createCustomer(data: CustomerFormData): Promise<Customer> {
    try {
      logger.info('Creating customer', { data });

      // Transform data to match backend DTO conventions
      const transformedData = transformcustomerData(data);

      // Send as JSON
      const response = await apiService.post(BASE_URL, transformedData);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to create customer', error);
      throw error;
    }
  }

  /**
   * Admin-upload a KYC document for a customer (e.g. right after creating an
   * Influencer profile from Influencer Network > Add). Same storage path
   * mobile self-registration uses (customer_document, PENDING) — just
   * reached via an admin-authenticated endpoint instead of the customer's
   * own token. See ManualApprovalAdminController.uploadDocument (backend).
   */
  async uploadInfluencerKycDocument(
    customerId: string,
    documentType: 'SELFIE' | 'AADHAAR_FRONT' | 'AADHAAR_BACK' | 'PAN_CARD',
    file: File,
    documentNumber?: string,
  ): Promise<{ documentId: string; documentUrl: string }> {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);
    if (documentNumber) formData.append('documentNumber', documentNumber);
    const response = await apiService.uploadFile<any>(
      `/loyalty/admin/manual-approval/${customerId}/documents`,
      formData,
    );
    return response?.data || response;
  }

  /**
   * Admin-save a customer's self-declared bank details (no OTP/penny-drop —
   * see ManualApprovalService.saveBankDetails). Submitting flips
   * bankVerified=true immediately, same as when the customer enters it
   * themselves from the app.
   */
  async saveInfluencerBankDetails(
    customerId: string,
    data: { accountNumber: string; confirmAccountNumber: string; ifscCode: string; accountHolderName?: string },
  ): Promise<{ success: boolean; bankVerified: boolean }> {
    const response = await apiService.post<any>(
      `/loyalty/admin/manual-approval/${customerId}/bank-details`,
      data,
    );
    return response?.data || response;
  }

  /**
   * Update existing customer
   * Endpoint: PUT /web/customer-master/:id
   */
  async updateCustomer(
    id: string | number,
    data: CustomerFormData
  ): Promise<Customer> {
    try {
      logger.info('Updating customer', { id, data });

      // Transform data to match backend DTO conventions
      const transformedData = transformcustomerData(data);

      // Send as JSON
      const response = await apiService.put(`${BASE_URL}/${id}`, transformedData);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to update customer', error);
      throw error;
    }
  }

  /**
   * Delete customer (soft delete)
   * Endpoint: DELETE /web/customer-master/:id
   */
  async deleteCustomer(id: string | number): Promise<void> {
    try {
      logger.info('Deleting customer', { id });

      await apiService.delete(`${BASE_URL}/${id}`);
    } catch (error) {
      logger.error('Failed to delete customer', error);
      throw error;
    }
  }

  /**
   * Restore a soft-deleted customer
   * Endpoint: POST /web/customer-master/:id/restore
   */
  async restoreCustomer(id: string | number): Promise<void> {
    try {
      logger.info('Restoring customer', { id });

      await apiService.post(`${BASE_URL}/${id}/restore`, {});
    } catch (error) {
      logger.error('Failed to restore customer', error);
      throw error;
    }
  }

  /**
   * Bulk delete customers
   * Endpoint: POST /web/customer-master/bulk-delete
   */
  async bulkDeleteParties(ids: (string | number)[]): Promise<void> {
    try {
      logger.info('Bulk deleting customers', { ids });

      await apiService.post(`${BASE_URL}/bulk-delete`, { ids });
    } catch (error) {
      logger.error('Failed to bulk delete customers', error);
      throw error;
    }
  }

  // ========== Statistics & Analytics ==========

  /**
   * Get customer statistics
   * Endpoint: GET /web/customer-master/stats
   * @param customerMainTypeId - Optional filter (1=Primary, 2=Secondary, 3=Tertiary, 4=Influencer)
   */
  async getCustomerStats(customerMainTypeId?: number): Promise<customerStats> {
    try {
      logger.info('Fetching customer statistics', { customerMainTypeId });

      const params = customerMainTypeId ? { customer_main_type_id: customerMainTypeId } : {};
      const response = await apiService.get(`${BASE_URL}/stats`, { params });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch customer stats', error);
      throw error;
    }
  }

  /**
   * Group customers by their optional salesperson attribution code.
   * Waterman India (org 29) only — see BaseCustomerList's ORGS_WITH_SALESPERSON_CODE gate.
   * Endpoint: GET /web/master/customer/salesperson-summary
   */
  async getSalespersonSummary(customerMainTypeId?: number): Promise<Array<{ salespersonCode: string; customerCount: number }>> {
    try {
      logger.info('Fetching salesperson summary', { customerMainTypeId });

      const params = customerMainTypeId ? { customer_main_type_id: customerMainTypeId } : {};
      const response = await apiService.get(`${BASE_URL}/salesperson-summary`, { params });

      return response?.data?.list || response?.list || [];
    } catch (error) {
      logger.error('Failed to fetch salesperson summary', error);
      throw error;
    }
  }

  // ========== Status Management ==========

  /**
   * Update customer status
   * Endpoint: PUT /web/customer-master/:id/status
   */
  async updateCustomerStatus(
    id: string | number,
    status: string
  ): Promise<Customer> {
    try {
      logger.info('Updating customer status', { id, status });

      const response = await apiService.put(`${BASE_URL}/${id}/status`, { status });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to update customer status', error);
      throw error;
    }
  }

  /**
   * Approve/Reject KYC
   * Endpoint: PATCH /web/customer-master/:id/kyc
   */
  async updateKYCStatus(
    id: string | number,
    kyc_status: 'APPROVED' | 'REJECTED',
    remarks?: string
  ): Promise<Customer> {
    try {
      logger.info('Updating KYC status', { id, kyc_status, remarks });

      const response = await apiService.patch(`${BASE_URL}/${id}/kyc`, {
        kyc_status,
        remarks,
      });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to update KYC status', error);
      throw error;
    }
  }

  /**
   * Update customer location (latitude/longitude)
   * Endpoint: PUT /web/master/customer/:id/location
   */
  async updateCustomerLocation(
    id: string | number,
    latitude: number,
    longitude: number
  ): Promise<{ id: string; latitude: number; longitude: number }> {
    try {
      logger.info('Updating customer location', { id, latitude, longitude });

      const response = await apiService.put(`${BASE_URL}/${id}/location`, {
        latitude,
        longitude,
      });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to update customer location', error);
      throw error;
    }
  }

  /**
   * Reset customer location (clear latitude/longitude)
   * Endpoint: DELETE /web/master/customer/:id/location
   */
  async resetCustomerLocation(
    id: string | number
  ): Promise<{ id: string; latitude: null; longitude: null }> {
    try {
      logger.info('Resetting customer location', { id });

      const response = await apiService.delete(`${BASE_URL}/${id}/location`);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to reset customer location', error);
      throw error;
    }
  }

  // ========== Import/Export (Background Jobs) ==========

  private readonly IMPORT_EXPORT_URL = '/web/master/customer/import-export';

  /**
   * Download import template
   * Endpoint: GET /web/master/customer/import-export/template/:customerType
   */
  async downloadImportTemplate(customerType: string): Promise<void> {
    try {
      logger.info('Downloading import template', { customerType });

      const response = await apiService.get(`${this.IMPORT_EXPORT_URL}/template/${customerType}`, {
        responseType: 'blob',
      });

      // Create download link
      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${customerType}_import_template.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Failed to download import template', error);
      throw error;
    }
  }

  /**
   * Start import job (background processing)
   * Endpoint: POST /web/master/customer/import-export/import
   */
  async startImportJob(data: {
    fileContent: string;
    fileName: string;
    importType: string;
    updateExisting?: boolean;
    skipDuplicates?: boolean;
    jobName?: string;
  }): Promise<{ jobId: string; message: string; totalRows: number }> {
    try {
      logger.info('Starting customer import job', { fileName: data.fileName, importType: data.importType });

      const response = await apiService.post(`${this.IMPORT_EXPORT_URL}/import`, data);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to start import job', error);
      throw error;
    }
  }

  /**
   * Start export job (background processing)
   * Endpoint: POST /web/master/customer/import-export/export
   */
  async startExportJob(data: {
    exportType: string;
    format?: string;
    filters?: Record<string, any>;
    selectedFields?: string[];
    jobName?: string;
  }): Promise<{ jobId: string; message: string }> {
    try {
      logger.info('Starting customer export job', { exportType: data.exportType });

      const response = await apiService.post(`${this.IMPORT_EXPORT_URL}/export`, data);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to start export job', error);
      throw error;
    }
  }

  /**
   * Get import/export job history
   * Endpoint: GET /web/master/customer/import-export/jobs
   */
  async getJobHistory(params?: {
    type?: 'import' | 'export';
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{ jobs: any[]; total: number }> {
    try {
      logger.info('Fetching job history', params);

      const response = await apiService.get(`${this.IMPORT_EXPORT_URL}/jobs`, { params });

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch job history', error);
      throw error;
    }
  }

  /**
   * Get job status
   * Endpoint: GET /web/master/customer/import-export/jobs/:jobId
   */
  async getJobStatus(jobId: string): Promise<any> {
    try {
      logger.info('Fetching job status', { jobId });

      const response = await apiService.get(`${this.IMPORT_EXPORT_URL}/jobs/${jobId}`);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to fetch job status', error);
      throw error;
    }
  }

  /**
   * Cancel job
   * Endpoint: POST /web/master/customer/import-export/jobs/:jobId/cancel
   */
  async cancelJob(jobId: string): Promise<any> {
    try {
      logger.info('Cancelling job', { jobId });

      const response = await apiService.post(`${this.IMPORT_EXPORT_URL}/jobs/${jobId}/cancel`);

      return response?.data || response;
    } catch (error) {
      logger.error('Failed to cancel job', error);
      throw error;
    }
  }

  /**
   * Get team members for customer filter (team multi-select)
   * Endpoint: GET /web/master/customer/team-members
   * Returns list of team members whose customer data the current user can access
   */
  async getTeamMembers(): Promise<Array<{ id: string; name: string; designation?: string }>> {
    try {
      logger.info('Fetching team members for filter');

      // Use data-access/accessible-users endpoint
      const response = await apiService.get(`/web/master/data-access/accessible-users`, {
        params: {
          viewScope: 'team',
          includeSubordinateCount: false
        }
      });

      // Extract users array from response
      const users = response?.data?.users || response?.users || [];

      // Transform to expected format
      return Array.isArray(users) ? users.map((user: any) => ({
        id: user.id || user.userId,
        name: user.name || user.userName || user.fullName,
        designation: user.designation || user.role
      })) : [];
    } catch (error) {
      logger.error('Failed to fetch team members', error);
      return [];
    }
  }

  // ========== Dropdown/Master Data Services ==========

  /**
   * Get customer main types
   * Endpoint: GET /web/customer-master/main-types
   */
  async getCustomerMainTypes(): Promise<CustomerMainType[]> {
    try {
      logger.info('Fetching customer main types');

      const response = await apiService.get(`${BASE_URL}/main-types`);

      return response?.data?.list || response?.data || response || [];
    } catch (error) {
      logger.error('Failed to fetch customer main types', error);
      return [];
    }
  }

  /**
   * Get customer sub types (filtered by main type)
   * Endpoint: GET /web/customer-master/sub-types
   */
  async getCustomerSubTypes(customerMainTypeId?: number): Promise<CustomerSubType[]> {
    try {
      logger.info('Fetching customer sub types', { customerMainTypeId });

      const params = customerMainTypeId ? { customer_main_type_id: customerMainTypeId } : {};
      const response = await apiService.get(`${BASE_URL}/sub-types`, { params });

      // After apiClient unwraps, response format may be { list: [...] } or direct array
      const list = response?.list || response?.data?.list || response?.data || response || [];

      return Array.isArray(list) ? list : [];
    } catch (error) {
      logger.error('Failed to fetch customer sub types', error);
      return [];
    }
  }

  /**
   * Get routes
   * Endpoint: GET /web/customer-master/routes
   */
  async getRoutes(): Promise<Route[]> {
    try {
      logger.info('Fetching routes');

      const response = await apiService.get(`${BASE_URL}/routes`);

      // After apiClient unwraps, response format is { list: [...] }
      const list = response?.list || response?.data?.list || response?.data || response || [];

      return Array.isArray(list) ? list : [];
    } catch (error) {
      logger.error('Failed to fetch routes', error);
      return [];
    }
  }

  /**
   * Get beats (filtered by route)
   * Endpoint: GET /web/customer-master/beats
   */
  async getBeats(routeId?: string): Promise<Beat[]> {
    try {
      logger.info('Fetching beats', { routeId });

      const params = routeId ? { route_id: routeId } : {};
      const response = await apiService.get(`${BASE_URL}/beats`, { params });

      return response?.data?.list || response?.data || response || [];
    } catch (error) {
      logger.error('Failed to fetch beats', error);
      return [];
    }
  }

  /**
   * Get sales persons
   * Endpoint: GET /web/customer-master/sales-persons
   */
  async getSalesPersons(): Promise<SalesPerson[]> {
    try {
      logger.info('Fetching sales persons');

      const response = await apiService.get(`${BASE_URL}/sales-persons`);

      // After apiClient unwraps, response format is { list: [...] }
      const list = response?.list || response?.data?.list || response?.data || response || [];

      return Array.isArray(list) ? list : [];
    } catch (error) {
      logger.error('Failed to fetch sales persons', error);
      return [];
    }
  }

  /**
   * Get states
   * Endpoint: GET /web/customer-master/states
   */
  async getStates(): Promise<string[]> {
    try {
      logger.info('Fetching states');

      const response = await apiService.get(`${BASE_URL}/states`);

      return response?.data?.list || response?.data || response || [];
    } catch (error) {
      logger.error('Failed to fetch states', error);
      return [];
    }
  }

  /**
   * Get cities by state
   * Endpoint: GET /web/customer-master/cities
   */
  async getCities(state?: string): Promise<string[]> {
    try {
      logger.info('Fetching cities', { state });

      const params = state ? { state } : {};
      const response = await apiService.get(`${BASE_URL}/cities`, { params });

      return response?.data?.list || response?.data || response || [];
    } catch (error) {
      logger.error('Failed to fetch cities', error);
      return [];
    }
  }

  /**
   * Get parent customers (for hierarchy)
   * Convenience method using list endpoint
   */
  async getParentParties(customerMainTypeId?: number): Promise<Customer[]> {
    try {
      logger.info('Fetching parent customers', { customerMainTypeId });

      const filters: CustomerListFilters = {
        status: 'active',
        ...(customerMainTypeId && { customer_main_type_id: customerMainTypeId }),
      };

      const response = await this.getCustomerList(1, 100, filters);

      return response.list || [];
    } catch (error) {
      logger.error('Failed to fetch parent customers', error);
      return [];
    }
  }

  /**
   * Get activities/check-ins for a customer
   * Endpoint: GET /web/master/customer/:customerId/activities
   */
  async getCustomerActivities(
    customerId: string,
    filters?: {
      startDate?: string;
      endDate?: string;
      userId?: string;
      status?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<any> {
    try {
      logger.info('Fetching customer activities', { customerId, filters });

      const response = await apiService.get(`${BASE_URL}/${customerId}/activities`, {
        params: filters,
      });

      return response || {};
    } catch (error) {
      logger.error('Failed to fetch customer activities', error);
      throw error;
    }
  }

  /**
   * Search customer suggestions for global autocomplete
   * Endpoint: GET /web/master/customer/search-suggestions
   * Searches across all customer types (Primary, Secondary, Tertiary, Influencer)
   */
  async searchcustomerSuggestions(
    search: string,
    limit: number = 10,
    teamUserIds?: string[]
  ): Promise<Array<{
    id: string;
    customerCode: string;
    customerName: string;
    mobile: string;
    customerMainTypeId: number;
    city?: string;
  }>> {
    try {
      logger.info('Fetching customer suggestions', { search, limit, teamUserIds });

      const params: Record<string, any> = { search, limit };
      if (teamUserIds && teamUserIds.length > 0) {
        params.team_user_ids = teamUserIds;
      }

      const response = await apiService.get(`${BASE_URL}/search-suggestions`, { params });

      return response?.data || response || [];
    } catch (error) {
      logger.error('Failed to fetch customer suggestions', error);
      return [];
    }
  }

  /**
   * Get territories (beat masters) for territory-based assignment
   * Endpoint: GET /web/beat-master
   * @param params - Optional search/filter parameters with pagination
   */
  async getTerritories(params?: {
    search?: string;
    state?: string;
    district?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    data: Territory[];
    pagination: {
      page: number;
      total: number;
      totalPages: number;
      hasNextPage: boolean;
    };
  }> {
    try {
      const queryParams: Record<string, any> = {
        page: params?.page || 1,
        limit: params?.limit || 20,
        isActive: true,
      };

      if (params?.search) {
        queryParams.search = params.search;
      }
      if (params?.state) {
        queryParams.state = params.state;
      }
      if (params?.district) {
        queryParams.district = params.district;
      }

      logger.info('Fetching territories with params:', queryParams);

      const response = await apiService.get('/web/beat-master', { params: queryParams });

      // Handle response format from beat-master API
      const data = response?.data?.list || response?.list || response?.data || response || [];
      const list = Array.isArray(data) ? data : [];

      // Extract pagination info from response
      const pagination = response?.data?.pagination || response?.pagination || {
        page: params?.page || 1,
        total: list.length,
        totalPages: 1,
        hasNextPage: false,
      };

      return {
        data: list.map((item: any) => ({
          id: item.id,
          uniqueId: item.uniqueId,
          beatRouteCode: item.beatRouteCode,
          beatName: item.beatName,
          state: item.state,
          district: item.district,
          city: item.city,
          area: item.area,
        })),
        pagination: {
          page: pagination.page || params?.page || 1,
          total: pagination.total || list.length,
          totalPages: pagination.totalPages || 1,
          hasNextPage: pagination.hasNextPage ?? false,
        },
      };
    } catch (error) {
      logger.error('Failed to fetch territories', error);
      return {
        data: [],
        pagination: { page: 1, total: 0, totalPages: 0, hasNextPage: false },
      };
    }
  }
  /**
   * Validate mobile number (check duplicate)
   * Endpoint: GET /shared/customer-master/validate/mobile/:mobile
   */
  async validateMobile(mobile: string): Promise<{ available: boolean; message: string }> {
    try {
      const response = await apiService.get(`/shared/customer-master/validate/mobile/${mobile}`);
      return response?.data || response || { available: true, message: '' };
    } catch (error) {
      logger.error('Failed to validate mobile', error);
      return { available: true, message: '' };
    }
  }
}

export const customerService = new CustomerServiceImpl();

// Standalone exports for convenience
export const getTerritories = customerService.getTerritories.bind(customerService);

export default customerService;
